import { useAppSelector, useAppDispatch } from "@/hooks";
import { auth } from "@/lib/firebase";
import { currentUser, setUser as setReduxUser, type User } from "@/store/appSlice";
import { onAuthStateChanged, type User as FirebaseUser } from "firebase/auth";
import { useEffect, useState, useRef } from "react";
import { Navigate, Outlet, useNavigate, useLocation } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import API from "@/utils/api";

interface UserWithClaims {
  uid: string;
  email: string | null;
  displayName: string | null;
  photoURL: string | null;
  claims?: Record<string, unknown>;
}

const RequireAuth = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useAppDispatch();
  const authUser = useAppSelector(currentUser) as UserWithClaims | null;
  const [user, setUser] = useState<UserWithClaims | null>(authUser);
  const [isChecking, setIsChecking] = useState(true);
  const previousSurveyStatusRef = useRef<boolean | null>(null);

  // Query to check survey completion status periodically
  const { data: surveyStatus } = useQuery<boolean>({
    queryKey: ["survey-completion", user?.email],
    queryFn: async () => {
      if (!user?.email) return false;
      try {
        const response = await API.post("check-completed-survey", { email: user.email });
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        const { data } = response.data;
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
        const isCompleted = data?.isCompleted as boolean | undefined;
        return Boolean(isCompleted);
      } catch (err) {
        console.error("Error checking survey completion:", err);
        return false;
      }
    },
    enabled: !!user?.email, // Only run when user is authenticated and has email
    refetchInterval: 30000, // Check every 30 seconds
    refetchOnWindowFocus: true, // Also check when window regains focus
    staleTime: 0, // Always consider data stale to ensure frequent checks
  });

  // Navigate to welcome page if survey is not completed
  useEffect(() => {
    if (user?.email && surveyStatus !== undefined) {
      // Only navigate if status changed from completed to not completed
      // or if it's the first check and survey is not completed
      if (
        !surveyStatus &&
        (previousSurveyStatusRef.current === null || previousSurveyStatusRef.current === true)
      ) {
        void navigate("/welcome", { replace: true });
      }
      previousSurveyStatusRef.current = surveyStatus;
    }
  }, [surveyStatus, user?.email, navigate]);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (firebaseUser: FirebaseUser | null) => {
      void (async () => {
        setIsChecking(true);

        if (firebaseUser) {
          try {
            const tokenResult = await firebaseUser.getIdTokenResult();
            const claims = tokenResult.claims;

            const userData: UserWithClaims = {
              uid: firebaseUser.uid,
              email: firebaseUser.email,
              displayName: firebaseUser.displayName,
              photoURL: firebaseUser.photoURL,
              claims,
            };

            setUser(userData);
            // Convert UserWithClaims to User (email can be null in Firebase but required in User)
            const reduxUser: User = {
              uid: userData.uid,
              email: userData.email ?? "",
              displayName: userData.displayName ?? "",
              photoURL: userData.photoURL ?? "",
              claims: userData.claims,
            };
            dispatch(setReduxUser(reduxUser));
            // Reset previous status when user changes
            previousSurveyStatusRef.current = null;
          } catch (error) {
            console.error("Error processing auth state:", error);
            setUser(null);
            dispatch(setReduxUser(null));
          }
        } else {
          setUser(null);
          dispatch(setReduxUser(null));
          previousSurveyStatusRef.current = null;
        }

        setIsChecking(false);
      })();
    });

    return () => unsubscribe();
  }, [dispatch]);

  if (isChecking) {
    return null; // or a loading spinner
  }

  if (!user) {
    return <Navigate to="/start" replace />;
  }

  // Check if user is paused
  const isPaused = user.claims?.isPause === 1 || user.claims?.isPause === true;
  const isSuspendedRoute = location.pathname === "/suspended";

  // If user is paused and trying to access a route other than /suspended, redirect to /suspended
  if (isPaused && !isSuspendedRoute) {
    return <Navigate to="/suspended" replace />;
  }

  // If user is paused and on /suspended route, allow access
  if (isPaused && isSuspendedRoute) {
    return <Outlet />;
  }

  // Normal flow for non-paused users
  return <Outlet />;
};

export default RequireAuth;
